import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCurrentUser from "../../../hooks/useCurrentUser";
import { setHasNotification, setNotificationCounter, syncNotification } from "../reducer";
import { usePlayNotificationSound } from "./usePlayNotificationSound";
import { Query } from "../../../types/firebase.types";
import { usePushSystemNotification } from "./usePushSystemNotification";
import { useCheckAgentType } from "../../../hooks/useCheckAgentType";
import { ReduxState } from "../../../types/redux.types";

export const useNotificationListListener = ({
  query,
  canGetNotifications = false,
  prepareNotifications,
  notificationCounter = 0,
  playSound,
  userData = {},
  onClick,
}: {
  query: Query;
  canGetNotifications?: boolean;
  prepareNotifications: (docs: any[]) => Promise<any[]>;
  notificationCounter?: number;
  playSound?: (data?: any) => boolean;
  userData?: Record<string, any>;
  onClick?: (...args: any[]) => void;
}) => {
  const dispatch = useDispatch();
  const { user } = useCurrentUser();
  const intervalId = useRef<any>();
  const muted = useSelector<ReduxState, boolean>((state) => state.notifications.muted);

  const agentType = useCheckAgentType();
  const playNotification = usePlayNotificationSound();
  const pushSystemNotification = usePushSystemNotification(userData);

  const [canPushNotification, setCanPushNotification] = useState(false);
  const [fetchedNotification, setFetchedNotification] = useState<Record<string, any>>({});
  const fetchedNotificationId = fetchedNotification.id;

  useEffect(() => {
    let unsubscribeToListener: any;

    if (canGetNotifications && user.id) {
      unsubscribeToListener = query.limit(1).onSnapshot({
        next: async (snapshot) => {
          if (snapshot.size) {
            try {
              const modifiedNotification: any = (await prepareNotifications(snapshot.docs))[0];
              console.log({ modifiedNotification });

              // only sync new added notification if user didn't see it
              setCanPushNotification(!modifiedNotification.seenBy.includes(user.id));
              setFetchedNotification(modifiedNotification);

              if (!modifiedNotification.seenBy.includes(user.id)) {
                dispatch(setNotificationCounter(notificationCounter));
              }
              dispatch(setHasNotification(!modifiedNotification.seenBy.includes(user.id)));
            } catch (ex: any) {
              console.log(ex.message);
            }
          }
        },
        error(ex: any) {
          console.log(ex.message);
          dispatch(
            syncNotification({
              notification: null,
              error: ex.message,
            })
          );
        },
      });
    }

    return () => {
      unsubscribeToListener?.();
    };
  }, []);

  useEffect(() => {
    // set notification bullet
    // if (canGetNotifications) {
    //   dispatch(setNotificationCounter(notificationCounter));
    // }
    // console.log({ canPushNotification });
    // dispatch(setHasNotification(canGetNotifications));
  }, [notificationCounter, dispatch, canGetNotifications]);

  useEffect(() => {
    if (canPushNotification && fetchedNotificationId) {
      dispatch(
        syncNotification({
          notification: fetchedNotification,
          error: null,
        })
      );
    }
  }, [canPushNotification, dispatch, fetchedNotificationId]);

  useEffect(() => {
    if (canPushNotification && fetchedNotificationId) {
      if (agentType.isWeb) pushSystemNotification(fetchedNotification, onClick);
    }
  }, [agentType.isWeb, canPushNotification, fetchedNotificationId, onClick, pushSystemNotification]);

  useEffect(() => {
    if (user.id && canPushNotification && fetchedNotificationId && !muted) {
      // run interval, to repeat the notification
      intervalId.current = setInterval(() => {
        if (playSound?.(fetchedNotification)) playNotification(muted);
      }, 1000);
    } else {
      if (intervalId.current) clearInterval(intervalId.current);
    }

    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
    };
  }, [canPushNotification, fetchedNotification, fetchedNotificationId, muted, playNotification, playSound, user.id]);
};
